import React, {useContext, useMemo, useRef} from 'react'
import {
  SActionBtn,
  SIDRender,
  SLowcodeList, SMessage,
  STableActions,
} from 'shj-components'
import {useSetState} from 'ahooks'

import type {DragEndEvent} from '@dnd-kit/core';
import {DndContext} from '@dnd-kit/core';
import {restrictToVerticalAxis} from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import type {SyntheticListenerMap} from '@dnd-kit/core/dist/hooks/utilities';

import AddOrEditModal from './components/addOrEditModal.tsx';
import {getConfig} from './config/index.tsx'
import {message, RowProps, Switch, Button} from "antd";
import {SwapOutlined} from "@ant-design/icons";
import {
  getSalesTemplatePage,
  salesTemplateDel,
  salesTemplateSort,
  salesTemplateUpdateState
} from "@/services/customerServices.ts";
import {apiData2Table} from "@/utils/data.ts";
import {httpOrgList} from "@/services/homeService.ts";
import DetailModal from "@/pages/salePlanTemplate/components/detail.tsx";

interface RowContextProps {
  setActivatorNodeRef?: (element: HTMLElement | null) => void;
  listeners?: SyntheticListenerMap;
}

const RowContext = React.createContext<RowContextProps>({});

interface RowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  "data-row-key": string;
}

const Row: React.FC<RowProps> = (props) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({id: props["data-row-key"]});

  const style: React.CSSProperties = {
    ...props.style,
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging ? {position: "relative", zIndex: 9999} : {}),
  };

  const contextValue = useMemo<RowContextProps>(
      () => ({setActivatorNodeRef, listeners}),
      [setActivatorNodeRef, listeners],
  );

  return (
      <RowContext.Provider value={contextValue}>
        <tr {...props} ref={setNodeRef} style={style} {...attributes} />
      </RowContext.Provider>
  );
};

const DragHandle: React.FC = () => {
  const {setActivatorNodeRef, listeners} = useContext(RowContext);
  return (
      <Button
          type="text"
          size="small"
          icon={<SwapOutlined style={{rotate: '90deg'}}/>}
          style={{cursor: 'move'}}
          ref={setActivatorNodeRef}
          {...listeners}
      />
  );
};

export default function SalePlanTemplate(props) {

  const [state, setState] = useSetState<any>({
    open: false,
    viewOpen: false,
    record: {},
    dataSource: [],
    companyList: [],
  })

  const listRef = useRef()

  const fetchList = ({current, pageSize, query}) => {
    return new Promise((resolve, reject) => {
      getSalesTemplatePage({
        params: {pageId: current, pageSize},
        data: {...query, templateType: props.templateType}
      }).then(res => {
        resolve(apiData2Table(res))
        setState({dataSource: res.list || []})
      }).catch(reject)
    })
  }

  const onAdd = () => onEdit()

  const onEdit = (record = {}) => {
    setState({open: true, record: {...record}})
  }

  const onChangeStatus = (enable: boolean, record) => {
    SMessage.confirm({
      title: '修改状态',
      content: `是否确认${enable ? '启用' : '禁用'}该数据`,
      onOk: () => {
        return salesTemplateUpdateState({...record, enable}).then((res: any) => {
          message.success(res.message || '操作成功');
          listRef.current?.reload()
        })
      },
    });
  }

  const handleCloseModal = () => {
    setState({open: false, viewOpen: false, record: {}})
  }

  const onView = (record) => {
    setState({viewOpen: true, record: {...record}})
  }

  const onDel = (id: string) => {
    SMessage.confirm({
      onOk: () => {
        return salesTemplateDel(id).then((res: any) => {
          message.success(res.message || '操作成功');
          listRef.current?.reload()
        })
      },
    });
  }

  const getOptions = async () => {
    const companyList = await httpOrgList();
    setState({companyList})
    return Promise.resolve({companyList})
  }

  const onDragEnd = ({active, over}: DragEndEvent) => {
    if (active.id !== over?.id) {
      const prev = state.dataSource;
      const activeIndex = prev.findIndex((i) => i.id === active.id);
      const overIndex = prev.findIndex((i) => i.id === over?.id);
      // changeSort([{id: active.id, weight: overIndex}])
      const newDataSource = arrayMove(prev, activeIndex, overIndex);
      const data = newDataSource.map((item, i) => ({id: item.id, weight: i}))
      changeSort(data)
      setState({
        dataSource: newDataSource,
      });
    }
  };

  const changeSort = (data) => {
    salesTemplateSort(data).then(res => {
      message.success(res.message || '操作成功');
      listRef.current?.reload()
    })
  }

  // 右侧表格配置
  const extensions = {
    request: {fetchList, getOptions},
    events: {onAdd},
    rowKey: 'id',
    dataSource: state.dataSource,
    components: {
      body: {
        row: Row,
      },
    },
    customRender: {
      IdRender(text: any, record: any) {
        return (
            <SIDRender id={text} onClick={() => onView(record)} copy={false}></SIDRender>
        )
      },
      switchRender(v: boolean, record: Record<string, any>) {
        return <Switch checkedChildren="启用" unCheckedChildren="禁用" disabled={v}
                       checked={v} onClick={(checked) => onChangeStatus(checked, record)}/>
      },
      dragRender() {
        return <DragHandle/>
      },
      actionRender(_, record: Record<string, any>) {
        return (
            <STableActions>
              {/*<SActionBtn onClick={() => onView(record)}> 详情 </SActionBtn>*/}
              <SActionBtn disabled={record.enable} onClick={() => onEdit(record)}> 编辑 </SActionBtn>
              <SActionBtn disabled={record.enable} danger onClick={() => onDel(record.id)}> 删除 </SActionBtn>
            </STableActions>
        )
      },
    },
  }

  return (<>
    <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
      <SortableContext
          // rowKey array
          items={state.dataSource.map((i) => i.id)}
          strategy={verticalListSortingStrategy}
      >
        <SLowcodeList ref={listRef} searchMode="group" config={getConfig(props.templateType)} {...extensions}/>
      </SortableContext>
    </DndContext>
    {state.viewOpen && <DetailModal {...props}
                                    record={state.record}
                                    companyList={state.companyList}
                                    onClose={handleCloseModal}
                                    open={state.viewOpen}
                                    fn={listRef.current?.reload}/>}
    <AddOrEditModal
        {...props}
        record={state.record}
        companyList={state.companyList}
        onClose={handleCloseModal}
        open={state.open}
        fn={listRef.current?.reload}
    />
  </>)
}